import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material";
import { QuestionsIF } from "./QuestionsIF";
import { DocQuestion } from "./DocQuestion";
import { QuestionOptionsContainer } from "./layout/questionOptionsContainer";
import {
  ConversationNextQuestionSuggestions,
  QualifiedFeature,
} from "fe-common/graphql";
import { PatientFacingQuestion } from "../../types/patientConversationAliasing";
import { GenericButton } from "../uiControllers/genericButton";
import { useTranslate } from "../../hooks/useTranslate";
import { QuestionButtonArea } from "./ClosedMultipleQuestionView";
import { NumericFeatureIds } from "./rangeQuestions/rangeIds";
import { QuestionElement } from "./questionElement";
import { onRtl } from "fe-web-components/rtl/rtlStyles";

//region [[ Styles ]]

const FromQuestionView = styled("div")({
  "& .range-input-area": {
    background: "transparent",
    padding: 0,
    margin: 0,
    borderRadius: 0,
    "& .range-content": {},
  },
});

const FormQuestionContainer = styled(QuestionOptionsContainer)({
  margin: 0,
  padding: "0 24px",
  ...onRtl({
    margin: 0,
    padding: "0 24px",
  }),
  background: "transparent",
  "& > *": {
    margin: "24px 0",
  },
  "& .question-button-area .submit-from-button": {
    width: "100%",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface FormQuestionProps extends QuestionsIF {}

//endregion [[ Props ]]

//region [[ Functions ]]
// Static wrapper function to avoid re-renders and call our handler with the index and the results
const createHandlerWithIndex = (index, handler) => (questionType) => (
  results,
) => handler(index, results);

//endregion [[ Functions ]]

export const FormQuestion = ({ question, ...props }: FormQuestionProps) => {
  const { translate } = useTranslate();

  const allowSkip = question.suggestions.every((s) => s.canBeUnknown);

  const [answers, setAnswers] = useState(
    new Array(question?.suggestions?.length || 0).fill(null),
  );
  const handleSuggestionAnswered = useCallback(
    (
      index: number,
      qfs: QualifiedFeature[],
      skipped: boolean,
      metadata: any,
    ) => {
      // console.log("FROM - onAnswer", qfs);
      if (!qfs) {
        // remove
        setAnswers((old) => [...old.slice(0, index), ...old.slice(index + 1)]);
      } else {
        setAnswers((old) => [
          ...old.slice(0, index),
          { qfs, skipped },
          ...old.slice(index + 1),
        ]);
      }
    },
    [],
  );

  const buildFormQuestionFromSuggestion: (
    suggestions: ConversationNextQuestionSuggestions[],
  ) => PatientFacingQuestion = useCallback(
    (suggestions) => ({
      ...question,
      suggestions,
      // If couple are sent, always take the first one
      questionType: suggestions?.[0].questionType,
      question: suggestions?.[0].question || question.question,
    }),
    [question],
  );

  useEffect(() => {
    setAnswers(new Array(question?.suggestions?.length).fill(null));
  }, [question]);

  const handleFormSubmit = () => {
    props.onAnswer(answers.filter((a) => !!a).flatMap((a) => a.qfs));
  };

  // We create memoized handlers for all suggestion indecencies so we avoid re-renders
  // which might cause infinite loops on update
  const memoizedHandlers = useMemo(() => {
    return question.suggestions.map((_, index) =>
      createHandlerWithIndex(index, handleSuggestionAnswered),
    );
  }, [handleSuggestionAnswered, question]);

  const toQuestion = useCallback(
    (suggestion: ConversationNextQuestionSuggestions, index, array) => {
      let suggestions = [suggestion];

      // If both systolic and diastolic are sent next to each other, render them as one questions
      if (
        suggestion.suggestion?.feature?.id ===
          NumericFeatureIds.SystolicBloodPressure &&
        index + 1 < array.length &&
        array[index + 1].suggestion?.feature?.id ===
          NumericFeatureIds.DiastolicBloodPressure
      ) {
        suggestions.push(array[index + 1]);
      }
      // if its diastolic coming after systolic, dont render it
      else if (
        suggestion.suggestion?.feature?.id ===
          NumericFeatureIds.DiastolicBloodPressure &&
        index > 0 &&
        array[index - 1].suggestion?.feature?.id ===
          NumericFeatureIds.SystolicBloodPressure
      ) {
        return null;
      }

      return (
        <QuestionElement
          key={suggestions[0].suggestion.id || index}
          index={index}
          onAnswer={memoizedHandlers[index]} // A hack to make sure the function is memoized and not re-created each time
          question={buildFormQuestionFromSuggestion(suggestions)}
          answer={null}
          //TODO: read and build previous answer from answer element and feature id?
          prevAns={null}
          miniView
        />
      );
    },
    [buildFormQuestionFromSuggestion, memoizedHandlers],
  );

  const submitButtonText =
    !allowSkip || answers.some((a) => a)
      ? translate("SUBMIT")
      : translate("SKIP");

  const isSubmitButtonDisabled =
    !allowSkip && (!answers.length || !answers.every((a) => a?.qfs));

  return (
    <FromQuestionView className={"form-question"}>
      <DocQuestion text={question.question?.text || translate("FORM_QUESTION")}>
        {!props.answer && (
          <FormQuestionContainer className={"form-question-container"}>
            {question.suggestions.map(toQuestion)}
            <QuestionButtonArea className={"question-button-area"}>
              <GenericButton
                className={"submit-from-button"}
                onClick={handleFormSubmit}
                label={submitButtonText}
                disabled={isSubmitButtonDisabled}
              />
            </QuestionButtonArea>
          </FormQuestionContainer>
        )}
      </DocQuestion>
    </FromQuestionView>
  );
};
